import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { Link } from "@/lib/nav";

import EmptyState from "@buildpy/ui/empty-state";
import FilterChips from "@/components/filter-chips";
import SectionCard from "@buildpy/ui/section-card";
import Money from "@/components/money";
import StatCard from "@/components/stat-card";
import { APP_TZ, formatNumber } from "@buildpy/core/lib/format";
import {
  currentReportMonth,
  type MonthlyReport,
  reportHref,
  reportMonthLabel,
  reportMonthOptions,
  reportRangeOf,
} from "@buildpy/core/modules/insight/monthly-report";
import type { InsightsReportView } from "@buildpy/core/views/g1a/insights-report";
import PageHeader from "@/shell/page-header";

dayjs.extend(utc);
dayjs.extend(timezone);

// 새 문의 경로 — 많은 순 세 개, 나머지는 「그 외」로 묶는다.
const SOURCE_HINT_MAX = 3;

const sourceHint = (report: MonthlyReport): string => {
  const rows = report.inquiries.bySource;
  if (rows.length === 0) return "들어온 문의가 없습니다";
  const head = rows.slice(0, SOURCE_HINT_MAX).map((r) => `${r.label} ${formatNumber(r.count)}`);
  const rest = rows.slice(SOURCE_HINT_MAX).reduce((sum, r) => sum + r.count, 0);
  return rest > 0 ? [...head, `그 외 ${formatNumber(rest)}`].join(" · ") : head.join(" · ");
};

// 월간 리포트 — 머리 · 네 숫자 · 아랫줄(일지·사진·시공사례). 달은 ?month=YYYY-MM, 기본은 지난달.
const Page = ({ data }: { data: InsightsReportView }) => {
  const { month, loaded } = data;
  const now = new Date(data.now);
  const range = reportRangeOf(month);

  const chips = reportMonthOptions(now).map((m) => ({
    href: reportHref(m, now),
    label: m === currentReportMonth(now) ? "이번 달" : reportMonthLabel(m, now),
  }));
  const monthChips = <FilterChips activeHref={reportHref(month, now)} aria-label="달" items={chips} />;
  const isCurrent = month === currentReportMonth(now);
  const last = isCurrent ? dayjs(now).tz(APP_TZ) : dayjs(range.to).tz(APP_TZ).subtract(1, "day");
  const caption = `${dayjs(range.from).tz(APP_TZ).format("MM-DD")} ~ ${last.format("MM-DD")}${isCurrent ? " · 오늘까지" : ""}`;

  if (!loaded) {
    return (
      <>
        <PageHeader actions={monthChips} title="월간 리포트" />
        <EmptyState>
          <span data-testid="report-failed">리포트를 불러오지 못했습니다. 잠시 뒤 다시 열어 주세요.</span>
        </EmptyState>
      </>
    );
  }

  const { org, report } = loaded;
  const { changeOrders, linkViews } = report;

  return (
    <>
      <PageHeader actions={monthChips} description={caption} title="월간 리포트" />

      <SectionCard aria-labelledby="report-title" data-testid="report" padding="md">
        <h2 className="mb-3 text-lg font-semibold" id="report-title">
          {org?.name ? `${org.name} · ` : ""}
          {reportMonthLabel(month, now)} 리포트
        </h2>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="report-metrics">
          <StatCard
            hint={`${formatNumber(changeOrders.count)}건 · 고객 링크 ${formatNumber(changeOrders.byLink)} · 직원 기록 ${formatNumber(changeOrders.byStaff)}`}
            title="고객이 승인한 변경공사"
            value={<Money value={changeOrders.amount} />}
          />
          <StatCard
            hint={`이달 처음 열린 링크 · 견적 ${formatNumber(linkViews.quoteFirstViews)} · 계약·내 현장 ${formatNumber(linkViews.contractFirstViews)}`}
            title="고객 링크 열람"
            value="집계 안 됨"
          />
          <StatCard hint={sourceHint(report)} title="새 문의" value={`${formatNumber(report.inquiries.total)}건`} />
          <StatCard hint="이달 체결 · 다시 쓴 계약 제외" title="견적 → 계약" value={`${formatNumber(report.contracts)}건`} />
        </div>

        <dl className="mt-4 flex flex-wrap gap-x-6 gap-y-2 border-t border-border pt-3 text-sm" data-testid="report-lower">
          <div className="flex gap-x-1.5">
            <dt className="text-muted-foreground">현장일지</dt>
            <dd className="font-medium tabular-nums">{formatNumber(report.logs)}건</dd>
          </div>
          <div className="flex gap-x-1.5">
            <dt className="text-muted-foreground">사진</dt>
            <dd className="font-medium tabular-nums">{formatNumber(report.photos)}장</dd>
          </div>
          <div className="flex gap-x-1.5">
            <dt className="text-muted-foreground">시공사례</dt>
            <dd className="font-medium tabular-nums">
              {formatNumber(report.portfolios.created)}건 등록 · 지금 공개 {formatNumber(report.portfolios.published)}건
            </dd>
          </div>
        </dl>
      </SectionCard>

      <p className="text-xs text-muted-foreground">
        고객 링크 열람 횟수는 열 때마다 시각을 남기지 않아 아직 세지 않습니다. 문의 경로별 계약까지는{" "}
        <Link className="underline underline-offset-2" href="/app/insights">
          유입 분석
        </Link>
        에서 봅니다.
      </p>
    </>
  );
};

export default Page;
